import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { useTeam } from '@/hooks/useTeam'
import Container from '@/components/common/Container'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Loading from '@/components/common/Loading'
import { toast } from 'sonner'
import { HOCKEY_AGE_GROUPS } from '@/lib/hockeyData'

/**
 * TeamSetup - Claim-First Onboarding
 * 
 * New users are guided to:
 * 1. Search for their team in the canonical database
 * 2. Claim an unclaimed team OR request access to a claimed team
 * 3. Request a team be added if not found (no self-service creation)
 * 4. Skip for now (can use scrimmage features without claiming)
 */
function TeamSetup() {
  const { user } = useAuth()
  const { team, loading: teamLoading } = useTeam()
  const navigate = useNavigate()
  
  const [step, setStep] = useState('level') // 'level', 'search', 'claim', 'request_access', 'request_add'
  const [selectedLevel, setSelectedLevel] = useState('')
  const [selectedLevelDetail, setSelectedLevelDetail] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedTeam, setSelectedTeam] = useState(null)
  
  // Request Add form state
  const [requestAddForm, setRequestAddForm] = useState({
    associationName: '',
    teamName: '',
    designation: '',
    calendarUrl: ''
  })

  useEffect(() => {
    // Redirect if team already exists
    if (!teamLoading && team) {
      navigate('/dashboard')
    }
  }, [team, teamLoading, navigate])

  const searchTeams = async () => {
    if (!selectedLevel) {
      toast.error('Please select an age group first')
      return
    }
    
    setLoading(true)
    try {
      const { data, error } = await supabase.rpc('search_claimable_teams', {
        search_term: searchQuery || null,
        filter_level: selectedLevel,
        filter_level_detail: selectedLevelDetail || null,
        filter_state: 'MN',
        max_results: 30
      })

      if (error) throw error
      setSearchResults(data || [])
      setStep('search')
    } catch (error) {
      console.error('Search error:', error)
      toast.error('Search failed - please try again')
    } finally {
      setLoading(false)
    }
  }

  const handleClaimTeam = async (teamToClaim) => {
    if (teamToClaim.is_claimed) {
      // Show request access flow
      setSelectedTeam(teamToClaim)
      setStep('request_access')
      return
    }
    
    // Proceed with claim
    setSelectedTeam(teamToClaim)
    setStep('claim')
  }

  const submitClaim = async (verificationMethod = 'first_claim') => {
    if (!selectedTeam || !user) return
    
    setLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        toast.error('Please sign in to claim a team')
        return
      }

      // Direct claim - first come, first served for unclaimed teams
      // No admin review needed unless there's a conflict later
      const { error: updateError } = await supabase
        .from('teams')
        .update({
          claimed_at: new Date().toISOString(),
          claimed_by: session.user.id,
          user_id: session.user.id,
          calendar_verified: verificationMethod === 'calendar_verified'
        })
        .eq('id', selectedTeam.id)
        .is('claimed_at', null) // Only if not already claimed

      if (updateError) throw updateError

      // Create scrimmage_manager record so contact info shows on bulletin
      const { data: existingManager } = await supabase
        .from('scrimmage_managers')
        .select('id')
        .eq('team_id', selectedTeam.id)
        .maybeSingle()

      if (!existingManager) {
        await supabase.from('scrimmage_managers').insert({
          team_id: selectedTeam.id,
          user_id: session.user.id,
          name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'Team Manager',
          email: user.email,
          display_email: true,
          display_phone: false
        })
      }

      // Log analytics (don't fail if this errors)
      try {
        await supabase.from('analytics_events').insert({
          event_name: 'team_claimed',
          user_id: session.user.id,
          team_id: selectedTeam.id,
          properties: {
            verification_method: verificationMethod,
            team_name: selectedTeam.name,
            association: selectedTeam.association_name
          }
        })
      } catch {
        // Ignore analytics errors
      }

      toast.success(`You've claimed ${selectedTeam.name}!`)
      navigate('/dashboard')
    } catch (error) {
      console.error('Claim error:', error)
      toast.error(error.message || 'Failed to claim team')
    } finally {
      setLoading(false)
    }
  }

  const submitAccessRequest = async () => {
    if (!selectedTeam || !user) return
    
    setLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        toast.error('Please sign in')
        return
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/request-team-access`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
          },
          body: JSON.stringify({ teamId: selectedTeam.id })
        }
      )

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || 'Request failed')
      }

      toast.success('Access request sent! The team manager will be notified.')
      navigate('/dashboard')
    } catch (error) {
      console.error('Access request error:', error)
      toast.error(error.message || 'Failed to send request')
    } finally {
      setLoading(false)
    }
  }

  const submitTeamAddRequest = async () => {
    if (!requestAddForm.associationName || !requestAddForm.teamName) {
      toast.error('Please fill in association and team name')
      return
    }
    
    setLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        toast.error('Please sign in')
        return
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/request-team-add`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
          },
          body: JSON.stringify({
            associationName: requestAddForm.associationName,
            teamName: requestAddForm.teamName,
            designation: requestAddForm.designation || null,
            calendarUrl: requestAddForm.calendarUrl || null,
            level: selectedLevel,
            levelDetail: selectedLevelDetail || null
          })
        }
      )

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || 'Request failed')
      }

      toast.success('Request submitted! We\'ll add your team and email you when it\'s ready to claim.')
      navigate('/dashboard')
    } catch (error) {
      console.error('Team add request error:', error)
      toast.error(error.message || 'Failed to submit request')
    } finally {
      setLoading(false)
    }
  }

  if (teamLoading) {
    return <Loading text="Checking team status..." />
  }

  return (
    <div className="min-h-screen bg-brand-cream py-12">
      <Container>
        <div className="max-w-2xl mx-auto">
          {/* Step 1: Select Level */}
          {step === 'level' && (
            <Card>
              <div className="mb-6">
                <h1 className="font-heading text-3xl font-bold mb-2">
                  Find Your Team
                </h1>
                <p className="text-brand-slate">
                  Let's find your team in our database. Start by selecting your age group.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="label">Age Group *</label>
                  <select
                    value={selectedLevel}
                    onChange={(e) => setSelectedLevel(e.target.value)}
                    className="input w-full"
                  >
                    <option value="">Select age group...</option>
                    {HOCKEY_AGE_GROUPS.map(ag => (
                      <option key={ag.value} value={ag.value}>{ag.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="label">Team Tier (optional)</label>
                  <select
                    value={selectedLevelDetail}
                    onChange={(e) => setSelectedLevelDetail(e.target.value)}
                    className="input w-full"
                  >
                    <option value="">All tiers</option>
                    <option value="A">A</option>
                    <option value="B1">B1</option>
                    <option value="B2">B2</option>
                    <option value="C">C</option>
                  </select>
                </div>

                <div>
                  <label className="label">Search by team or association name</label>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="e.g., Wayzata, CCHA, Rogers..."
                    className="input w-full"
                  />
                </div>

                <Button
                  variant="primary"
                  className="w-full"
                  onClick={searchTeams}
                  disabled={!selectedLevel || loading}
                  loading={loading}
                >
                  Search Teams
                </Button>

                <div className="text-center pt-4 border-t border-border mt-6">
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="text-sm text-brand-slate hover:text-brand-charcoal"
                  >
                    Skip for now — I'll find my team later
                  </button>
                </div>
              </div>
            </Card>
          )}

          {/* Step 2: Search Results */}
          {step === 'search' && (
            <Card>
              <button 
                onClick={() => setStep('level')} 
                className="text-sm text-brand-slate hover:text-brand-charcoal mb-4"
              >
                ← Back to search
              </button>

              <h2 className="font-heading text-xl font-bold mb-4">
                {searchResults.length} team{searchResults.length !== 1 ? 's' : ''} found
              </h2>

              {searchResults.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {searchResults.map(t => (
                    <div 
                      key={t.id}
                      className={`p-4 border rounded-lg transition-colors ${
                        t.is_claimed 
                          ? 'border-amber-300 bg-amber-50' 
                          : 'border-gray-200 hover:border-brand-green'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-brand-charcoal">{t.name}</h3>
                            {t.is_claimed && (
                              <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">
                                Claimed
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-brand-slate">{t.association_name}</p>
                          <p className="text-xs text-brand-slate">
                            {t.city}, {t.state} • {t.level_detail || ''}
                            {t.designation && ` ${t.designation}`}
                          </p>
                          {t.is_claimed && t.claimed_by_name && (
                            <p className="text-xs text-amber-700 mt-1">
                              Managed by {t.claimed_by_name}
                            </p>
                          )}
                        </div>
                        <Button
                          variant={t.is_claimed ? "outline" : "primary"}
                          size="sm"
                          onClick={() => handleClaimTeam(t)}
                        >
                          {t.is_claimed ? 'Request Access' : 'Claim'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-brand-slate">No teams found matching your search.</p>
              )}

              <div className="mt-6 pt-4 border-t border-border">
                <p className="text-sm text-brand-slate mb-3">
                  Don't see your team?
                </p>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setStep('request_add')}
                >
                  Request Team Be Added
                </Button>
              </div>

              <div className="text-center pt-4">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="text-sm text-brand-slate hover:text-brand-charcoal"
                >
                  Skip for now
                </button>
              </div>
            </Card>
          )}

          {/* Step 3: Claim Team - Direct claim for unclaimed teams */}
          {step === 'claim' && selectedTeam && (
            <Card>
              <button 
                onClick={() => setStep('search')} 
                className="text-sm text-brand-slate hover:text-brand-charcoal mb-4"
              >
                ← Back to results
              </button>

              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🏒</span>
                </div>
                <h2 className="font-heading text-xl font-bold mb-2">
                  Claim {selectedTeam.name}
                </h2>
                <p className="text-sm text-brand-slate">
                  {selectedTeam.association_name}
                </p>
                {selectedTeam.calendar_sync_url && (
                  <p className="text-xs text-brand-slate mt-2 truncate" title={selectedTeam.calendar_sync_url}>
                    📅 {selectedTeam.calendar_sync_url.substring(0, 50)}...
                  </p>
                )}
              </div>

              <p className="text-brand-slate text-sm mb-4">
                This team hasn't been claimed yet. Claim it now to manage scrimmage postings and team communications.
              </p>

              <Button
                variant="primary"
                className="w-full"
                onClick={() => submitClaim('first_claim')}
                loading={loading}
              >
                Claim This Team
              </Button>

              <p className="text-xs text-brand-slate text-center mt-4">
                You'll be able to link your team calendar after claiming.
              </p>
            </Card>
          )}

          {/* Step 4: Request Access */}
          {step === 'request_access' && selectedTeam && (
            <Card>
              <button 
                onClick={() => setStep('search')} 
                className="text-sm text-brand-slate hover:text-brand-charcoal mb-4"
              >
                ← Back to results
              </button>

              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">👋</span>
                </div>
                <h2 className="font-heading text-xl font-bold mb-2">
                  Request Team Access
                </h2>
                <p className="text-brand-slate">
                  <strong>{selectedTeam.name}</strong> is managed by {selectedTeam.claimed_by_name || 'another user'}.
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-amber-800 font-medium mb-2">What happens next:</p>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• The current manager will receive your request</li>
                  <li>• They can add you as a co-manager</li>
                  <li>• You'll be notified when they respond</li>
                </ul>
              </div>

              <div className="space-y-3">
                <Button
                  variant="primary"
                  className="w-full"
                  onClick={submitAccessRequest}
                  loading={loading}
                >
                  Send Access Request
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setStep('search')}
                >
                  Cancel
                </Button>
              </div>
            </Card>
          )}

          {/* Step 5: Request Team Be Added */}
          {step === 'request_add' && (
            <Card>
              <button 
                onClick={() => setStep('search')} 
                className="text-sm text-brand-slate hover:text-brand-charcoal mb-4"
              >
                ← Back to results
              </button>

              <div className="mb-6">
                <h2 className="font-heading text-xl font-bold mb-2">
                  Request Team Be Added
                </h2>
                <p className="text-brand-slate text-sm">
                  Tell us about your team and we'll add it to our database. You'll be notified when it's ready to claim.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="label">Association Name *</label>
                  <input
                    type="text"
                    value={requestAddForm.associationName}
                    onChange={(e) => setRequestAddForm(f => ({ ...f, associationName: e.target.value }))}
                    placeholder="e.g., Wayzata Youth Hockey Association"
                    className="input w-full"
                  />
                </div>

                <div>
                  <label className="label">Team Name *</label>
                  <input
                    type="text"
                    value={requestAddForm.teamName}
                    onChange={(e) => setRequestAddForm(f => ({ ...f, teamName: e.target.value }))}
                    placeholder="e.g., Squirt B1 Gold"
                    className="input w-full"
                  />
                </div>

                <div>
                  <label className="label">Designation (optional)</label>
                  <input
                    type="text"
                    value={requestAddForm.designation}
                    onChange={(e) => setRequestAddForm(f => ({ ...f, designation: e.target.value }))}
                    placeholder="e.g., Gold, Black, Blue, 1, 2..."
                    className="input w-full"
                  />
                </div>

                <div>
                  <label className="label">Calendar URL (optional, helps us verify)</label>
                  <input
                    type="url"
                    value={requestAddForm.calendarUrl}
                    onChange={(e) => setRequestAddForm(f => ({ ...f, calendarUrl: e.target.value }))}
                    placeholder="e.g., https://www.association.com/team/123/calendar"
                    className="input w-full"
                  />
                  <p className="text-xs text-brand-slate mt-1">
                    You can find this on your team's page on SportsEngine, TeamSnap, etc.
                  </p>
                </div>

                <Button
                  variant="primary"
                  className="w-full"
                  onClick={submitTeamAddRequest}
                  loading={loading}
                  disabled={!requestAddForm.associationName || !requestAddForm.teamName}
                >
                  Submit Request
                </Button>
              </div>
            </Card>
          )}
        </div>
      </Container>
    </div>
  )
}

export default TeamSetup
